/**
 * Base abstract class for grammar check providers
 */

import type { 
  IGrammarCheckProvider, 
  ProviderConfig, 
  ConnectionTestResult, 
  ValidationResult, 
  CheckOptions,
  ProviderType 
} from './types'
import type { GrammarError } from '$lib/types/grammar'

export abstract class BaseProvider implements IGrammarCheckProvider {
  protected config: ProviderConfig
  protected apiKey?: string
  protected serverUrl?: string

  constructor(config?: ProviderConfig) {
    this.config = config || { type: this.getType() }
    this.apiKey = config?.apiKey
    this.serverUrl = config?.serverUrl
  }

  /**
   * Initialize provider with configuration
   */
  initialize(config: ProviderConfig): void {
    const validation = this.validateConfig(config)
    if (!validation.valid) {
      throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`)
    }
    
    this.config = config
    this.apiKey = config.apiKey
    this.serverUrl = config.serverUrl
  }

  /**
   * Default configuration validation
   */
  validateConfig(config: ProviderConfig): ValidationResult {
    const errors: string[] = []

    // Check provider type matches
    if (config.type !== this.getType()) {
      errors.push(`Provider type mismatch: expected ${this.getType()}, got ${config.type}`)
    }

    // Check required API key
    if (this.requiresApiKey() && !config.apiKey) {
      errors.push('API key is required')
    }

    // Check required server URL
    if (this.requiresServerUrl() && !config.serverUrl) {
      errors.push('Server URL is required')
    }

    // Validate server URL format
    if (config.serverUrl) {
      try {
        new URL(config.serverUrl)
      } catch {
        errors.push('Invalid server URL format')
      }
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * Default connection test implementation
   */
  async testConnection(): Promise<ConnectionTestResult> {
    const startTime = Date.now()
    
    try {
      // Try to check a simple test text
      const testText = '테스트'
      await this.checkText(testText)
      
      return {
        success: true,
        message: '연결 성공',
        responseTime: Date.now() - startTime
      }
    } catch (error) {
      return {
        success: false,
        message: '연결 실패',
        responseTime: Date.now() - startTime,
        error: error instanceof Error ? error.message : '알 수 없는 오류'
      }
    }
  }

  /**
   * Helper method to make HTTP requests
   */
  protected async makeRequest(url: string, options: RequestInit, externalSignal?: AbortSignal): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

    // Listen to external abort signal if provided
    if (externalSignal) {
      externalSignal.addEventListener('abort', () => {
        controller.abort()
        clearTimeout(timeoutId)
      })
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(externalSignal?.aborted ? '요청이 취소되었습니다' : '요청 시간 초과')
      }
      
      throw error
    }
  }

  /**
   * Generate unique ID for errors
   */
  protected generateId(): string {
    return `${this.getType()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Map error level based on provider-specific logic
   */
  protected mapErrorLevel(severity: any): 'error' | 'warning' | 'suggestion' {
    // Default implementation - can be overridden by subclasses
    return 'error'
  }

  // Abstract methods that must be implemented by subclasses
  abstract checkText(text: string, options?: CheckOptions): Promise<GrammarError[]>
  abstract getName(): string
  abstract getDescription(): string
  abstract requiresApiKey(): boolean
  abstract requiresServerUrl(): boolean
  abstract getType(): ProviderType
}